import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNav from './AdminNav';
import ShoppingExperienceFeedback from './ShoppingExperienceFeedback';
import './AdminDashboard.css';

import { API, authHeadersOnly } from '../services/apiConfig';

function AdminDashboard() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProducts: 0,
    totalOrders: 0,
    totalRevenue: 0,
    lowStockCount: 0,
    lowStockProducts: [],
    totalEnterpriseInquiries: 0,
    totalSubscribers: 0
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const [dashRes, entRes, subRes] = await Promise.allSettled([
        fetch(`${API}/api/products/dashboard`, { headers: authHeadersOnly() }),
        fetch(`${API}/api/enterprise`, { headers: authHeadersOnly() }),
        fetch(`${API}/api/subscribers`, { headers: authHeadersOnly() }),
      ]);
      const data = dashRes.status === 'fulfilled' && dashRes.value.ok ? await dashRes.value.json() : {};
      const entData = entRes.status === 'fulfilled' && entRes.value.ok ? await entRes.value.json() : [];
      const subData = subRes.status === 'fulfilled' && subRes.value.ok ? await subRes.value.json() : [];
      setStats({
        totalUsers: data.totalUsers ?? 0,
        totalProducts: data.totalProducts ?? 0,
        totalOrders: data.totalOrders ?? 0,
        totalRevenue: data.totalRevenue ?? 0,
        lowStockCount: data.lowStockCount ?? 0,
        lowStockProducts: data.lowStockProducts ?? [],
        totalEnterpriseInquiries: Array.isArray(entData) ? entData.length : (entData.content?.length ?? 0),
        totalSubscribers: Array.isArray(subData) ? subData.length : 0,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  if (loading) {
    return (
      <>
        <AdminNav onLogout={handleLogout} />
        <div className="admin-dashboard-wrapper">
          <div className="admin-dashboard-container" style={{ textAlign: 'center', padding: '100px' }}>
            <div style={{ fontSize: '48px', color: '#667eea' }}>⏳</div>
            <p style={{ fontSize: '18px', color: '#888', marginTop: '20px' }}>Loading dashboard...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AdminNav onLogout={handleLogout} />
      <div className="admin-dashboard-wrapper">
        <div className="admin-dashboard-container">
          
          {/* Hero Header Section */}
          <div className="admin-hero-section">
            <h1 className="admin-hero-title">Admin Dashboard</h1>
            <p className="admin-hero-subtitle">Manage users, products, orders and reports</p>
          </div>

          {/* Premium Stat Cards */}
          <div className="admin-stats-grid">
            <div className="admin-stat-card users" onClick={() => navigate('/admin/users')}>
              <div className="admin-stat-icon-wrapper">
                <i className="fas fa-users admin-stat-icon"></i>
              </div>
              <div className="admin-stat-value">{stats.totalUsers}</div>
              <div className="admin-stat-label">Total Users</div>
            </div>

            <div className="admin-stat-card products" onClick={() => navigate('/admin/products')}>
              <div className="admin-stat-icon-wrapper">
                <i className="fas fa-box admin-stat-icon"></i>
              </div>
              <div className="admin-stat-value">{stats.totalProducts}</div>
              <div className="admin-stat-label">Total Products</div>
            </div>

            <div className="admin-stat-card orders" onClick={() => navigate('/admin/orders')}>
              <div className="admin-stat-icon-wrapper">
                <i className="fas fa-shopping-cart admin-stat-icon"></i>
              </div>
              <div className="admin-stat-value">{stats.totalOrders}</div>
              <div className="admin-stat-label">Total Orders</div>
            </div>

            <div className="admin-stat-card revenue">
              <div className="admin-stat-icon-wrapper">
                <i className="fas fa-rupee-sign admin-stat-icon"></i>
              </div>
              <div className="admin-stat-value">₹{stats.totalRevenue ? Number(stats.totalRevenue).toFixed(2) : '0.00'}</div>
              <div className="admin-stat-label">Total Revenue</div>
            </div>

            <div className="admin-stat-card enterprise" onClick={() => navigate('/admin/enterprise')}>
              <div className="admin-stat-icon-wrapper">
                <i className="fas fa-building admin-stat-icon"></i>
              </div>
              <div className="admin-stat-value">{stats.totalEnterpriseInquiries}</div>
              <div className="admin-stat-label">Enterprise Inquiries</div>
            </div>

            <div className="admin-stat-card" style={{ background: 'linear-gradient(135deg, white, white)', cursor: 'pointer' }} onClick={() => navigate('/admin/subscribers')}>
              <div className="admin-stat-icon-wrapper">
                <i className="fas fa-envelope admin-stat-icon"></i>
              </div>
              <div className="admin-stat-value">{stats.totalSubscribers}</div>
              <div className="admin-stat-label">Newsletter Subscribers</div>
            </div>
          </div>

          {/* Low Stock Alert */}
          {stats.lowStockCount > 0 && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 87, 34, 0.1) 100%)',
              padding: '25px',
              borderRadius: '20px',
              border: '2px solid rgba(255, 152, 0, 0.3)',
              marginBottom: '35px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                <i className="fas fa-exclamation-triangle" style={{ fontSize: '32px', color: '#ff9800' }}></i>
                <div>
                  <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#333' }}>
                    ⚠️ Low Stock Alert
                  </h3>
                  <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
                    {stats.lowStockCount} product(s) running low on stock
                  </p>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>
                {stats.lowStockProducts?.slice(0, 4).map((product) => (
                  <div key={product.productId} style={{
                    background: 'white',
                    padding: '15px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                  }}>
                    <p style={{ margin: 0, fontWeight: '700', fontSize: '14px', color: '#333' }}>{product.name}</p>
                    <p style={{ margin: '5px 0', fontSize: '12px', color: '#999' }}>{product.category}</p>
                    <p style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#ff5722' }}>
                      Stock: {product.quantity}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Actions Section */}
          <div className="admin-quick-actions">
            <h3 className="admin-quick-actions-title">Quick Actions</h3>
            <div className="admin-action-buttons">
              <button className="admin-action-btn" onClick={() => navigate('/admin/users')}>
                Manage Users
              </button>
              <button className="admin-action-btn" onClick={() => navigate('/admin/products')}>
                Manage Products
              </button>
              <button className="admin-action-btn" onClick={() => navigate('/admin/orders')}>
                Manage Orders
              </button>
              <button className="admin-action-btn" onClick={() => navigate('/admin/reviews')}>
                Ratings & Reviews
              </button>
              <button className="admin-action-btn" onClick={() => navigate('/admin/enterprise')}>
                Enterprise Inquiries
              </button>
              <button className="admin-action-btn" onClick={() => navigate('/admin/subscribers')}>
                Newsletter Subscribers
              </button>
              <button className="admin-action-btn" onClick={() => navigate('/admin/email-campaigns')}>
                Email Campaigns
              </button>
              <button className="admin-action-btn" onClick={() => navigate('/admin/reports')}>
                View Reports
              </button>
              <button className="admin-action-btn" onClick={() => navigate('/admin/contact-queries')}>
                Customer Queries
              </button>
            </div>
          </div>

          {/* Shopping Experience Feedback */}
          <ShoppingExperienceFeedback />

        </div>
      </div>
    </>
  );
}

export default AdminDashboard;
